import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { searchItems } from "../search";

interface SearchResult {
  type: "action" | "page" | "product" | "supplier" | "order" | "recent" | "pinned";
  label: string;
  sublabel?: string;
  icon: string;
  id?: string;
  action: () => void;
}

const PAGES_INDEX = [
  { route: "/dashboard", label: "Tổng quan", icon: "dashboard" },
  { route: "/orders", label: "Đơn hàng", icon: "receipt_long" },
  { route: "/sales-history", label: "Lịch sử bán", icon: "history" },
  { route: "/finance/expenses", label: "Chi phí", icon: "payments" },
  { route: "/finance/payables", label: "Công nợ", icon: "account_balance" },
  { route: "/tax/report", label: "Báo cáo thuế", icon: "request_quote" },
  { route: "/tax/purchase-ledger", label: "Bảng kê 01/TNDN", icon: "list_alt" },
  { route: "/finance/pnl", label: "Lãi / Lỗ", icon: "analytics" },
  { route: "/finance/revenue", label: "Báo cáo doanh thu", icon: "trending_up" },
  { route: "/finance/suppliers", label: "Nhà cung cấp", icon: "store" },
  { route: "/products", label: "Sản phẩm", icon: "inventory_2" },
  { route: "/inventory", label: "Tồn kho", icon: "monitoring" },
  { route: "/warehouse/virtual", label: "Kho ảo", icon: "category" },
  { route: "/warehouse/import", label: "Nhập kho (tiểu ngạch)", icon: "local_shipping" },
  { route: "/warehouse/import-vn", label: "Nhập hàng VN", icon: "local_shipping" },
  { route: "/inventory/take", label: "Kiểm kê", icon: "fact_check" },
  { route: "/warehouse/consignment", label: "Hàng ký gửi", icon: "handshake" },
  { route: "/warehouse/allocation", label: "Phân loại tồn", icon: "pie_chart" },
  { route: "/warehouse/backfill", label: "Điều chỉnh tồn", icon: "tune" },
  { route: "/inventory/predictive-restock", label: "Dự đoán đặt hàng", icon: "psychology" },
  { route: "/inventory/abc-analysis", label: "Phân tích ABC", icon: "analytics" },
  { route: "/ops/pos", label: "POS", icon: "point_of_sale" },
  { route: "/ops/barcode", label: "In tem barcode", icon: "qr_code_2" },
  { route: "/ops/todos", label: "Việc cần làm", icon: "checklist" },
  { route: "/ops/backup", label: "Sao lưu", icon: "cloud_upload" },
  { route: "/ops/assets", label: "Tài sản", icon: "business_center" },
  { route: "/ops/ai-config", label: "Cấu hình AI", icon: "smart_toy" },
  { route: "/ops/admin", label: "Quản trị", icon: "admin_panel_settings" },
];

interface ActionDef {
  label: string;
  icon: string;
  handler: (navigate: (path: string) => void) => void;
}

const ACTIONS: ActionDef[] = [
  { label: "Tạo chi phí mới", icon: "add_card", handler: (nav) => nav("/finance/expenses?action=new") },
  { label: "Thêm NCC mới", icon: "person_add", handler: (nav) => nav("/finance/suppliers?action=new") },
  { label: "Đồng bộ Loyverse", icon: "sync", handler: () => {
    const headers = api.authHeaders();
    fetch(`${api.baseUrl}/api/sync/full`, { method: "POST", headers, signal: AbortSignal.timeout(60000) }).catch(() => {});
  }},
  { label: "In tem barcode", icon: "qr_code_2", handler: (nav) => nav("/ops/barcode") },
  { label: "Mở backup mới nhất", icon: "cloud_download", handler: (nav) => nav("/ops/backup") },
  { label: "Tạo đơn nhập tiểu ngạch", icon: "add_shopping_cart", handler: (nav) => nav("/warehouse/import") },
  { label: "Tạo đơn nhập VN / VAT", icon: "add_shopping_cart", handler: (nav) => nav("/warehouse/import-vn") },
  { label: "Thêm sản phẩm mới", icon: "add_box", handler: (nav) => nav("/products?action=new") },
];

// Recent pages — stored in localStorage
function getRecent(): { route: string; label: string; ts: number }[] {
  try {
    const raw = localStorage.getItem("cmd_recent");
    if (!raw) return [];
    const items = JSON.parse(raw) as { route: string; label: string; ts: number }[];
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return items.filter(i => i.ts > sevenDaysAgo).slice(0, 5);
  } catch { return []; }
}

function addRecent(route: string, label: string) {
  const items = getRecent().filter(i => i.route !== route);
  items.unshift({ route, label, ts: Date.now() });
  localStorage.setItem("cmd_recent", JSON.stringify(items.slice(0, 5)));
}

// Pinned pages
function getPinned(): string[] {
  try {
    const raw = localStorage.getItem("cmd_pinned");
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function togglePin(route: string) {
  const pins = getPinned();
  const idx = pins.indexOf(route);
  if (idx >= 0) pins.splice(idx, 1);
  else pins.push(route);
  localStorage.setItem("cmd_pinned", JSON.stringify(pins));
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selected, setSelected] = useState(0);
  const [pinned, setPinned] = useState<string[]>(getPinned());
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRequestRef = useRef(0);
  const navigate = useNavigate();

  // Expose open function globally for Topbar integration
  useEffect(() => {
    (window as any).__openCommandPalette = () => setOpen(true);
    return () => { delete (window as any).__openCommandPalette; };
  }, []);

  // Open shortcut: Ctrl+K, Cmd+K, or / (slash)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(o => !o);
      }
      // Slash key opens palette (only when not in input/textarea)
      if (e.key === "/" && !open) {
        const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
        if (tag !== "input" && tag !== "textarea" && tag !== "select") {
          e.preventDefault();
          setOpen(true);
        }
      }
      if (e.key === "Escape" && open) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery("");
      setSelected(0);
      setPinned(getPinned());
    }
  }, [open]);

  const doNavigate = useCallback((route: string, label: string) => {
    addRecent(route, label);
    navigate(route);
    setOpen(false);
  }, [navigate]);

  // Search
  useEffect(() => {
    const requestId = ++searchRequestRef.current;
    if (!open) return;
    const q = query.toLowerCase().trim();

    const matchText = (label: string) => searchItems(
      [{ label }],
      q,
      (item) => ({ name: item.label }),
    ).length > 0;

    // Build results
    const out: SearchResult[] = [];

    if (!q) {
      // Show pinned first
      const pins = getPinned();
      for (const route of pins) {
        const page = PAGES_INDEX.find(p => p.route === route);
        if (page) {
          out.push({
            type: "pinned",
            label: page.label,
            sublabel: "Ghim",
            icon: "push_pin",
            id: page.route,
            action: () => doNavigate(page.route, page.label),
          });
        }
      }

      // Then recent
      const recent = getRecent();
      for (const r of recent) {
        if (pins.includes(r.route)) continue;
        const page = PAGES_INDEX.find(p => p.route === r.route);
        out.push({
          type: "recent",
          label: r.label,
          sublabel: "Gần đây",
          icon: page?.icon || "history",
          id: r.route,
          action: () => doNavigate(r.route, r.label),
        });
      }

      // Then actions
      for (const a of ACTIONS.slice(0, 4)) {
        out.push({
          type: "action",
          label: a.label,
          icon: a.icon,
          action: () => { a.handler(navigate); setOpen(false); },
        });
      }

      setResults(out);
      return;
    }

    // Actions matching query
    const matchedActions = ACTIONS.filter(a => matchText(a.label));
    for (const a of matchedActions.slice(0, 3)) {
      out.push({
        type: "action",
        label: a.label,
        sublabel: "Hành động",
        icon: a.icon,
        action: () => { a.handler(navigate); setOpen(false); },
      });
    }

    // Pages matching query
    const matchedPages = PAGES_INDEX.filter(p => matchText(p.label));
    for (const p of matchedPages.slice(0, 6)) {
      out.push({
        type: "page",
        label: p.label,
        icon: p.icon,
        id: p.route,
        action: () => doNavigate(p.route, p.label),
      });
    }

    // Entity search (debounced)
    const t = setTimeout(async () => {
      const entityResults: SearchResult[] = [...out];

      // Search products
      try {
        const res = await api.items.search(q);
        for (const p of (res.items || []).slice(0, 4)) {
          entityResults.push({
            type: "product",
            label: p.item_name,
            sublabel: `SP · ${p.category_name || ""}`,
            icon: "inventory_2",
            action: () => doNavigate("/products", p.item_name),
          });
        }
      } catch {}

      // Search suppliers
      try {
        const headers = api.authHeaders();
        const res = await fetch(`${api.baseUrl}/api/suppliers?q=${encodeURIComponent(q)}`, { headers });
        if (res.ok) {
          const data = await res.json() as any;
          for (const s of (data.suppliers || []).slice(0, 3)) {
            entityResults.push({
              type: "supplier",
              label: s.name,
              sublabel: "Nhà cung cấp",
              icon: "store",
              action: () => doNavigate("/finance/suppliers", s.name),
            });
          }
        }
      } catch {}

      // Search orders by number (#1042)
      if (q.startsWith("#") || /^\d{3,}$/.test(q)) {
        const orderNum = q.replace("#", "");
        try {
          const headers = api.authHeaders();
          const res = await fetch(`${api.baseUrl}/api/orders?q=${encodeURIComponent(orderNum)}&limit=3`, { headers });
          if (res.ok) {
            const data = await res.json() as any;
            for (const o of (data.orders || []).slice(0, 3)) {
              entityResults.push({
                type: "order",
                label: `Đơn #${o.receipt_number || o.order_number || orderNum}`,
                sublabel: o.total ? `${Number(o.total).toLocaleString()}đ` : "Đơn hàng",
                icon: "receipt_long",
                action: () => doNavigate(`/orders/${o.id || ""}`, `Đơn #${orderNum}`),
              });
            }
          }
        } catch {}
      }

      if (searchRequestRef.current === requestId) setResults(entityResults);
    }, 250);

    // Set immediate results (actions + pages) right away
    setResults(out);

    return () => clearTimeout(t);
  }, [query, open, navigate, doNavigate]);

  // Keyboard nav
  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelected(s => Math.min(s + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); results[selected]?.action(); }
  };

  const handlePin = (e: React.MouseEvent, route: string) => {
    e.stopPropagation();
    togglePin(route);
    setPinned(getPinned());
  };

  if (!open) return null;

  return createPortal(
    <div className="cmd-overlay" onClick={() => setOpen(false)}>
      <div className="cmd-palette" onClick={e => e.stopPropagation()}>
        <div className="cmd-input-wrap">
          <span className="material-symbols-outlined">search</span>
          <input
            ref={inputRef}
            className="cmd-input"
            placeholder="Tìm trang, hành động, sản phẩm, NCC... (/ hoặc ESC)"
            value={query}
            onChange={e => { setQuery(e.target.value); setSelected(0); }}
            onKeyDown={onInputKey}
          />
          <kbd className="cmd-kbd">ESC</kbd>
        </div>
        <div className="cmd-results">
          {results.length === 0 && <div className="cmd-empty">Không có kết quả</div>}
          {results.map((r, i) => (
            <button
              key={`${r.type}-${r.label}-${i}`}
              className={`cmd-result ${i === selected ? "selected" : ""}`}
              onClick={r.action}
              onMouseEnter={() => setSelected(i)}
            >
              <span className="material-symbols-outlined">{r.icon}</span>
              <div className="cmd-result-text">
                <div className="cmd-result-label">{r.label}</div>
                {r.sublabel && <div className="cmd-result-sublabel">{r.sublabel}</div>}
              </div>
              {r.id && (
                <span
                  className={`material-symbols-outlined cmd-pin ${pinned.includes(r.id) ? "pinned" : ""}`}
                  onClick={(e) => handlePin(e, r.id!)}
                  title={pinned.includes(r.id) ? "Bỏ ghim" : "Ghim"}
                >
                  {pinned.includes(r.id) ? "push_pin" : "push_pin"}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="cmd-footer">
          <span><kbd>↑↓</kbd> điều hướng</span>
          <span><kbd>↵</kbd> chọn</span>
          <span><kbd>/</kbd> mở</span>
          <span><kbd>ESC</kbd> đóng</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
